import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type { TicketBillingFlags } from '../types/billing-flags';

/**
 * The billing pre-flight flags on one ticket (api#908), shared by the panel
 * and the hint beside Office Approve so the two read one cache entry and make
 * one request.
 *
 * The roles mirror the API's FLAG_READ_ROLES / FLAG_ANSWER_ROLES. A role
 * outside the read set never makes the request: the panel is not theirs, and
 * a 403 in the request log for every ticket a field worker opens is noise.
 */
const READ_ROLES = new Set(['admin', 'owner', 'office_staff', 'supervisor']);
const ANSWER_ROLES = new Set(['admin', 'owner', 'office_staff']);

export const canReadBillingFlags = (role: string | null | undefined) => !!role && READ_ROLES.has(role);
export const canAnswerBillingFlags = (role: string | null | undefined) => !!role && ANSWER_ROLES.has(role);

export const billingFlagsKey = (ticketId: string | undefined) => ['billing-flags', ticketId] as const;

/**
 * Where the ticket is in its life, as far as the panel cares.
 *
 *   'open'      field_approved: the office is deciding, so open flags are actions
 *   'answered'  past office approval: answered flags only, read-only
 *   null        any other status: no panel and no request
 *
 * The API runs the checks only on a field_approved ticket, so a draft or a
 * submitted ticket has nothing to show.
 */
export type BillingFlagsStage = 'open' | 'answered';
const ANSWERED_STAGE_STATUSES = new Set(['office_approved', 'customer_approved', 'approved', 'invoiced']);

export function billingFlagsStage(status: string | null | undefined): BillingFlagsStage | null {
  if (status === 'field_approved') return 'open';
  if (status && ANSWERED_STAGE_STATUSES.has(status)) return 'answered';
  return null;
}

export function useTicketBillingFlags(
  ticketId: string | undefined,
  ticketStatus: string | null | undefined,
  role: string | null | undefined,
) {
  return useQuery({
    queryKey: billingFlagsKey(ticketId),
    queryFn: async () => unwrap<TicketBillingFlags>((await api.billingFlags.list(ticketId!)).data),
    enabled: !!ticketId && canReadBillingFlags(role) && billingFlagsStage(ticketStatus) !== null,
    // Retries a dead server, never a refusal, and keeps the failure where the
    // panel can see it rather than parking the query in `paused`.
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/**
 * Open flags the office should see. Zero outside `suggest`: in shadow the API
 * already sends `flags: []`, and if it ever sent rows there they would still
 * not be the office's to see.
 */
export function openBillingFlagCount(data: TicketBillingFlags | null | undefined): number {
  if (!data || data.mode !== 'suggest') return 0;
  return data.flags.filter(f => f.status === 'open').length;
}
